import { backgroundColor } from 'ag-grid-community/dist/types/src/agStack/theming/themeUtils';
import { apiGet, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, initFlatpickrInShadow, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme, updateSelectedSymbolInDropdown } from './../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';
import flatpickr from 'flatpickr';
import { text } from 'stream/consumers';

export class TraderverseIPOPriceDistribution {
    private container: HTMLElement;
    private config: FinancialConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private datePickerInstance: any = null;
    private startDate: string | null = null;
    private endDate: string | null = null;
    constructor(config: FinancialConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<FinancialConfig> {
        return {
      headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        this.attachStyles();
        await this.setupChart(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
     <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} IPO Price Distribution </h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)} <div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div> </div>
        </div>
        <div class="inBody">
            <div class="optionsRow">
                <div class="item">
                    <div class="ipo-statsRow">
                        <div class="statCard">
                            <p>Mean</p>
                            <div id="meanVal" class="c1">-</div>
                        </div>
                        <div class="statCard">
                            <p>Median</p>
                            <div id="medianVal" class="c2">-</div>
                        </div>
                        <div class="statCard">
                            <p>Min</p>
                            <div id="minVal" class="c3">-</div>
                        </div>
                        <div class="statCard">
                            <p>Max</p>
                            <div id="maxVal" class="c4">-</div>
                        </div>
                    </div>
                </div>
                <span></span>

                <div class="filtersBox sideBar hidden">
                    <div class="item">
                        <label>Bins:</label>
                        <div class="fieldsGroup">
                            <select id="binsPreset">
                                <option value="">Custom / default</option>
                                <option value="10,20,50">$10, $20, $50</option>
                                <option value="5,10,20,50">$5, $10, $20, $50</option>
                                <option value="0,5,10,15,20,25,30,40,50">Default Dark</option>
                            </select>
                            <input id="binsCustom" placeholder="e.g. 0,5,10,15" />
                        </div>
                    </div>
                    <div class="item">
                        <label>Outlier SD:</label>
                        <input id="outlierThreshold" type="number" step="0.1" value="2" />
                    </div>
                    <div class="item">
                        <div class="checkBoxField">
                            <input id="showStatistics" type="checkbox" checked />
                            <label>Show Stats: <i>✓</i></label>
                        </div>
                    </div>
                    <div class="item">
                        <label>Exchange:</label>
                        <select id="exchangeFilter">
                            <option value="ALL">ALL</option>
                            <option value="NYSE">NYSE</option>
                            <option value="NASDAQ">NASDAQ</option>
                        </select>
                    </div>
                    <div class="item">
                        <label>Date Range:</label>
                        <div class="dateRange">
                            <input id="dateRange" type="text" class="date-input" />
                            <i><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.91667 1.16667H8.75V0.583333C8.75 0.428624 8.68854 0.280251 8.57915 0.170854C8.46975 0.0614581 8.32138 0 8.16667 0C8.01196 0 7.86358 0.0614581 7.75419 0.170854C7.64479 0.280251 7.58333 0.428624 7.58333 0.583333V1.16667H4.08333V0.583333C4.08333 0.428624 4.02188 0.280251 3.91248 0.170854C3.80308 0.0614581 3.65471 0 3.5 0C3.34529 0 3.19692 0.0614581 3.08752 0.170854C2.97813 0.280251 2.91667 0.428624 2.91667 0.583333V1.16667H1.75C1.28587 1.16667 0.840752 1.35104 0.512563 1.67923C0.184375 2.00742 0 2.45254 0 2.91667V9.91667C0 10.3808 0.184375 10.8259 0.512563 11.1541C0.840752 11.4823 1.28587 11.6667 1.75 11.6667H9.91667C10.3808 11.6667 10.8259 11.4823 11.1541 11.1541C11.4823 10.8259 11.6667 10.3808 11.6667 9.91667V2.91667C11.6667 2.45254 11.4823 2.00742 11.1541 1.67923C10.8259 1.35104 10.3808 1.16667 9.91667 1.16667ZM10.5 9.91667C10.5 10.0714 10.4385 10.2198 10.3291 10.3291C10.2198 10.4385 10.0714 10.5 9.91667 10.5H1.75C1.59529 10.5 1.44692 10.4385 1.33752 10.3291C1.22812 10.2198 1.16667 10.0714 1.16667 9.91667V5.83333H10.5V9.91667ZM10.5 4.66667H1.16667V2.91667C1.16667 2.76196 1.22812 2.61358 1.33752 2.50419C1.44692 2.39479 1.59529 2.33333 1.75 2.33333H2.91667V2.91667C2.91667 3.07138 2.97813 3.21975 3.08752 3.32915C3.19692 3.43854 3.34529 3.5 3.5 3.5C3.65471 3.5 3.80308 3.43854 3.91248 3.32915C4.02188 3.21975 4.08333 3.07138 4.08333 2.91667V2.33333H7.58333V2.91667C7.58333 3.07138 7.64479 3.21975 7.75419 3.32915C7.86358 3.43854 8.01196 3.5 8.16667 3.5C8.32138 3.5 8.46975 3.43854 8.57915 3.32915C8.68854 3.21975 8.75 3.07138 8.75 2.91667V2.33333H9.91667C10.0714 2.33333 10.2198 2.39479 10.3291 2.50419C10.4385 2.61358 10.5 2.76196 10.5 2.91667V4.66667Z" fill="#9F9FA2"/> </svg> </i>
                        </div>
                    </div>
                </div>
            </div>

            <div class="chartDv" id="aiAnalysisScoreDistribution">
                <div id="chartContainer" class="chart coverDv"></div>
            </div>
        </div>
        </div>
        `;
        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
        if (!this.startDate && !this.endDate) {
            const now = new Date();
            const year = now.getFullYear();
            this.startDate = `${year}-01-01`;
            this.endDate = now.toISOString().slice(0, 10);
        }
        this.initDatePicker();
        try {
            const input = this.shadowRoot.querySelector('.date-input') as HTMLInputElement | null;
            if (input) {
                if (this.startDate && this.endDate) input.value = `${this.startDate} to ${this.endDate}`;
                else if (this.startDate) input.value = this.startDate;
            }
            if (this.datePickerInstance && this.datePickerInstance.setDate) {
                const defaults: string[] = [];
                if (this.startDate) defaults.push(this.startDate);
                if (this.endDate) defaults.push(this.endDate);
                if (defaults.length) this.datePickerInstance.setDate(defaults);
            }
            const preset = this.shadowRoot.querySelector('#binsPreset') as HTMLSelectElement | null;
            const custom = this.shadowRoot.querySelector('#binsCustom') as HTMLInputElement | null;
            try {
                if (preset && custom) {
                    const defaultBins = '10,20,50';
                    if (!preset.value) preset.value = defaultBins;
                    if (!custom.value) custom.value = defaultBins;
                }
            } catch (err) { /* ignore */ }
        } catch (err) {
        }
        const intervalEl = this.shadowRoot.querySelector('#intervalSelect') as HTMLSelectElement | null;
        if (intervalEl) {
            intervalEl.value = intervalEl.value || 'thisweek';
            intervalEl.addEventListener('change', () => {
                try { this.setupChart(this.config.symbol!); } catch (e) { console.error(e); }
            });
        }
        const binsPreset = this.shadowRoot.querySelector('#binsPreset') as HTMLSelectElement | null;
        const binsCustom = this.shadowRoot.querySelector('#binsCustom') as HTMLInputElement | null;
        const outlierEl = this.shadowRoot.querySelector('#outlierThreshold') as HTMLInputElement | null;
        const showStatsEl = this.shadowRoot.querySelector('#showStatistics') as HTMLInputElement | null;
        const exchangeEl = this.shadowRoot.querySelector('#exchangeFilter') as HTMLSelectElement | null;

        const triggerUpdate = () => { try { this.setupChart(this.config.symbol!); } catch (e) { console.error(e); } };

        if (binsPreset && binsCustom) {
            binsPreset.addEventListener('change', () => {
                if (binsPreset.value) binsCustom.value = binsPreset.value;
                triggerUpdate();
            });
            binsCustom.addEventListener('change', () => { binsPreset.value = ''; triggerUpdate(); });
        }
        if (outlierEl) outlierEl.addEventListener('change', triggerUpdate);
        if (showStatsEl) showStatsEl.addEventListener('change', triggerUpdate);
        if (exchangeEl) exchangeEl.addEventListener('change', triggerUpdate);
        const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
        const filtersBoxEl = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        if (filtersToggle && filtersBoxEl) {
            filtersToggle.addEventListener('click', (ev: Event) => {
                ev.stopPropagation();
                filtersBoxEl.classList.toggle('hidden');
            });
        }
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private initDatePicker(): void {
        try {
            const input = this.shadowRoot.querySelector('.date-input') as HTMLInputElement | null;
            if (!input) return;

            if (this.datePickerInstance && this.datePickerInstance.destroy) {
                this.datePickerInstance.destroy();
                this.datePickerInstance = null;
            }

            const defaultDates: string[] = [];
            if (this.startDate) defaultDates.push(this.startDate);
            if (this.endDate) defaultDates.push(this.endDate);

            this.datePickerInstance = initFlatpickrInShadow(input, this.shadowRoot, {
                mode: 'range',
                dateFormat: 'Y-m-d',
                defaultDate: defaultDates.length ? defaultDates : undefined,
                onClose: (selectedDates: Date[]) => {
                    if (selectedDates.length === 2) {
                        const s = selectedDates[0].toISOString().slice(0, 10);
                        const e = selectedDates[1].toISOString().slice(0, 10);
                        this.startDate = s;
                        this.endDate = e;
                        input.value = `${s} to ${e}`;
                        try { this.setupChart(this.config.symbol!); } catch (e) { /* ignore */ }
                    } else if (selectedDates.length === 1) {
                        const s = selectedDates[0].toISOString().slice(0, 10);
                        this.startDate = s;
                        this.endDate = s;
                        input.value = s;
                        try { this.setupChart(this.config.symbol!); } catch (e) { /* ignore */ }
                    }
                }
            });
        } catch (err) {
            console.warn('Date picker init failed:', err);
        }
    }


    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(symbol: string): Promise<void> {
        try {
            if ((this.config as any).data) {
                this.currentData = (this.config as any).data;
                this.updateDisplay();
                this.emit('dataUpdate', this.currentData);
                return;
            }

            const interval = (this.shadowRoot.querySelector('#intervalSelect') as HTMLSelectElement)?.value || 'thismonth';

            const binsCustomEl = this.shadowRoot.querySelector('#binsCustom') as HTMLInputElement | null;
            const binsPresetEl = this.shadowRoot.querySelector('#binsPreset') as HTMLSelectElement | null;
            const outlierEl = this.shadowRoot.querySelector('#outlierThreshold') as HTMLInputElement | null;
            const showStatsEl = this.shadowRoot.querySelector('#showStatistics') as HTMLInputElement | null;
            const exchangeEl = this.shadowRoot.querySelector('#exchangeFilter') as HTMLSelectElement | null;

            const params: string[] = [];
            if (this.startDate) params.push(`p_start_date=${encodeURIComponent(this.startDate)}`);
            if (this.endDate) params.push(`p_end_date=${encodeURIComponent(this.endDate)}`);

            const binsVal = (binsCustomEl && binsCustomEl.value) ? binsCustomEl.value : (binsPresetEl && binsPresetEl.value ? binsPresetEl.value : '');
            if (binsVal) {
                const bins = binsVal.split(',').map(s => s.trim()).filter(s => s !== '');
                for (const b of bins) params.push(`p_distribution_bins=${encodeURIComponent(b)}`);
            }

            const outlier = outlierEl ? outlierEl.value : '2';
            if (outlier) params.push(`p_outlier_threshold=${encodeURIComponent(outlier)}`);

            const showStats = showStatsEl && showStatsEl.checked ? 'true' : 'false';
            params.push(`p_show_statistics=${encodeURIComponent(showStats)}`);

            if (exchangeEl && exchangeEl.value && exchangeEl.value !== 'ALL') params.push(`p_exchange_filter=${encodeURIComponent(exchangeEl.value)}`);

            const query = params.join('&');
            const url = `${APIURL}/v1/discover/ipo-price-distribution${query ? '?' + query : ''}`;

            const res = await apiGet({
                method: 'GET',
                url,
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();

            this.currentData = json.result || json.data || json;

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
        }
    }


    private updateDisplay(): void {
            const chartDv = this.shadowRoot.querySelector('#aiAnalysisScoreDistribution') as HTMLElement;

        if (!this.currentData || !(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
            if (chartDv) {
        chartDv.classList.remove('hidden');
        chartDv.setAttribute('data-testing-id', 'IPOPrice Distribution');
    }

        this.renderSentimentChart();
    }


    private renderSentimentChart(): void {
        const container = this.shadowRoot.querySelector('#aiAnalysisScoreDistribution');
        if (!container) return;

        const html = `
            
        `;
        this.drawSentimentChart();
    }

    private drawSentimentChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;

        if (!this.currentData) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        const resp = this.currentData;
        const distribution = resp.distribution || resp.data?.distribution || [];
        const statistics = resp.statistics || resp.data?.statistics || {};

        if (!distribution || distribution.length === 0) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        const meanEl = this.shadowRoot.querySelector('#meanVal');
        const medianEl = this.shadowRoot.querySelector('#medianVal');
        const minEl = this.shadowRoot.querySelector('#minVal');
        const maxEl = this.shadowRoot.querySelector('#maxVal');
        const fmt = (v: any) => (typeof v === 'number' ? `$${v.toFixed(2)}` : v);
        if (meanEl) meanEl.textContent = fmt(statistics.mean_price ?? resp.mean_price ?? '-');
        if (medianEl) medianEl.textContent = fmt(statistics.median_price ?? resp.median_price ?? '-');
        if (minEl) minEl.textContent = fmt(statistics.min_price ?? resp.min_price ?? '-');
        if (maxEl) maxEl.textContent = fmt(statistics.max_price ?? resp.max_price ?? '-');

        const xAxisData = distribution.map((d: any) => d.price_range);
        const yAxisData = distribution.map((d: any) => d.count);

        // const isBlack = this.config.theme === 'black';
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
        const bgColor = getComputedStyle(widgetBox).getPropertyValue('--base-dark');

        const option = {
            backgroundColor: 'transparent',
            animation: true,
            grid: { left: 0, right: 0, top: 10, bottom: 0, containLabel: true },
            tooltip: {
                trigger: 'item',
                backgroundColor: bgColor,
                textStyle: {
                    color: textColor,
                    fontSize: fontSize
                },
                formatter: (params: any) => {
                    const d = distribution[params.dataIndex];
                    return `<b>${d.price_range}</b><br/>Count: ${d.count}`;
                }
            },
            xAxis: {
                type: 'category',
                data: xAxisData,
                axisLine: { lineStyle: { color: axisLineColor } },
                axisLabel: { color: textColor, fontSize: fontSize },
            },
            yAxis: {
                type: 'value',
                axisLine: { show: false },
                splitLine: { show: false },
                axisLabel: { color: textColor, fontSize: fontSize }
            },
            series: [{
                name: 'Count',
                type: 'bar',
                data: yAxisData,
                barWidth: '48%',
                itemStyle: {
                    borderRadius: [6, 6, 6, 6],
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: '#7a00ff' },
                        { offset: 1, color: '#4c00d6' }
                    ])
                }
            }]
        };

        const chart = echarts.init(container, null, {
            backgroundColor: getComputedStyle(widget).getPropertyValue('--bg-primary').trim()
        });

        chart.setOption(option);
        this.currentChart = chart;
    }



    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
    }

    public updateWithResponse(resp: any): void {
        if (!resp) return;
        this.currentData = resp;
        this.updateDisplay();
        this.emit('dataUpdate', resp);
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseIPOPriceDistribution: typeof TraderverseIPOPriceDistribution; } }
if (typeof window !== 'undefined') { window.TraderverseIPOPriceDistribution = TraderverseIPOPriceDistribution; }